/* =========================================================================
   James Aston — The House Search (type-ahead)
   Progressive enhancement over the native search input. As a shopper types,
   it queries Shopify's predictive search across product TITLE and TAGS, so a
   fragment such as "vac", "wal", "croc" or "ital" surfaces the right pieces
   through the House vocabulary written onto them. Self-contained, no deps.
   Nothing here changes the existing search; it only adds a panel beneath it.
   ========================================================================= */
(function () {
  'use strict';

  var FIELDS = 'title,product_type,variants.title,vendor,tag';
  var LIMIT  = 8;   // Baymard: keep desktop suggestions to about ten
  var MIN    = 2;   // begin after two letters
  var WAIT   = 170; // debounce, ms

  var CSS = ''
    + '.ja-hs{position:absolute;left:0;right:0;top:100%;z-index:10060;background:#FBFAF4;'
    + 'border:1px solid #E4DEC9;border-top:2px solid #C8A96A;box-shadow:0 16px 40px rgba(32,64,75,.16);'
    + 'display:none;max-height:72vh;overflow:auto;text-align:left;margin-top:2px;}'
    + '.ja-hs.is-open{display:block;}'
    + '.ja-hs__item{display:flex;align-items:center;gap:14px;padding:11px 16px;'
    + 'border-bottom:1px solid #EFE9DA;text-decoration:none;color:#20404B;}'
    + '.ja-hs__item:last-of-type{border-bottom:0;}'
    + '.ja-hs__item:hover,.ja-hs__item.is-active{background:#EBEBD3;}'
    + '.ja-hs__thumb{width:48px;height:48px;flex:0 0 48px;background:#F1ECDE center/cover no-repeat;'
    + 'border:1px solid #E4DEC9;}'
    + '.ja-hs__info{display:flex;flex-direction:column;min-width:0;}'
    + ".ja-hs__name{font-family:'Cormorant Garamond',Georgia,serif;font-size:17px;line-height:1.2;"
    + 'color:#20404B;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}'
    + ".ja-hs__price{font-family:'Cinzel',serif;font-size:12px;letter-spacing:.05em;color:#846A3A;margin-top:3px;}"
    + ".ja-hs__all{display:block;padding:12px 16px;text-align:center;background:#F3EFE3;"
    + "font-family:'Cinzel',serif;font-size:11px;letter-spacing:.18em;text-transform:uppercase;"
    + 'color:#20404B;text-decoration:none;}'
    + '.ja-hs__all:hover{background:#EBEBD3;}'
    + ".ja-hs__empty{padding:18px 16px;font-family:'Cormorant Garamond',Georgia,serif;font-style:italic;"
    + 'color:#57646B;font-size:17px;}';

  var st = document.createElement('style');
  st.setAttribute('data-ja-house-search', '');
  st.textContent = CSS;
  document.head.appendChild(st);

  function money(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    try { return '₹' + Math.round(n).toLocaleString('en-IN'); }
    catch (e) { return '₹' + Math.round(n); }
  }
  function esc(t) {
    return (t == null ? '' : '' + t).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function imgOf(p) {
    var u = p.image || p.featured_image || '';
    if (u && typeof u === 'object') u = u.url || u.src || '';
    return u || '';
  }
  function root() {
    var r = (window.theme && window.theme.routes && window.theme.routes.predictive_search_url) || '/search/suggest';
    return r;
  }

  function attach(input) {
    if (input.__jaHS) return;
    input.__jaHS = true;
    input.setAttribute('autocomplete', 'off');

    var holder = input.closest('.input-holder') || input.parentElement;
    if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';

    var panel = document.createElement('div');
    panel.className = 'ja-hs';
    panel.setAttribute('role', 'listbox');
    holder.appendChild(panel);

    var timer, last = '';

    function close() { panel.classList.remove('is-open'); }
    function open() { if (panel.innerHTML) panel.classList.add('is-open'); }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < MIN) { panel.innerHTML = ''; close(); return; }
      timer = setTimeout(function () { run(q); }, WAIT);
    });
    input.addEventListener('focus', open);
    input.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    document.addEventListener('click', function (e) { if (!holder.contains(e.target)) close(); });

    function run(q) {
      if (q === last && panel.innerHTML) { open(); return; }
      last = q;
      var url = root() + '.json?q=' + encodeURIComponent(q)
        + '&resources[type]=product&resources[limit]=' + LIMIT
        + '&resources[options][unavailable_products]=last'
        + '&resources[options][fields]=' + encodeURIComponent(FIELDS);
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          var list = (j.resources && j.resources.results && j.resources.results.products) || [];
          render(list, q);
        })
        .catch(function () { /* stay quiet; native submit still works */ });
    }

    function render(list, q) {
      if (!list.length) {
        panel.innerHTML = '<div class="ja-hs__empty">No pieces yet for “' + esc(q) + '”.</div>';
        open();
        return;
      }
      var html = '';
      for (var i = 0; i < list.length; i++) {
        var p = list[i];
        var u = imgOf(p);
        html += '<a class="ja-hs__item" href="' + esc(p.url) + '">'
          + '<span class="ja-hs__thumb"' + (u ? ' style="background-image:url(' + esc(u) + ')"' : '') + '></span>'
          + '<span class="ja-hs__info">'
          + '<span class="ja-hs__name">' + esc(p.title) + '</span>'
          + '<span class="ja-hs__price">' + money(p.price) + '</span>'
          + '</span></a>';
      }
      html += '<a class="ja-hs__all" href="/search?q=' + encodeURIComponent(q) + '&options[prefix]=last">'
        + 'View all results for “' + esc(q) + '”</a>';
      panel.innerHTML = html;
      open();
    }
  }

  function scan() {
    var els = document.querySelectorAll('input[name="q"][type="search"], input.search-box[name="q"]');
    for (var i = 0; i < els.length; i++) attach(els[i]);
  }

  if (document.readyState !== 'loading') scan();
  document.addEventListener('DOMContentLoaded', scan);
  try {
    new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) {}
})();
